import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  BadgeCheck,
  Banknote,
  Mail,
  Pencil,
  Phone,
  ScanLine,
  Search,
  Send,
  Ticket,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SeatEditor } from './SeatEditor.tsx';
import { adminApi } from './auth.ts';
import { fmtDateTime, parseScan } from './lib/format.ts';
import type { ReservationListItem } from './lib/types.ts';
import {
  findByReference,
  useReservationDetail,
  useReservationSearch,
} from './lib/useReservation.ts';
import { Badge, StatusBadge } from './ui/Badge.tsx';
import { Button } from './ui/Button.tsx';
import { EmptyState } from './ui/EmptyState.tsx';
import { Modal } from './ui/Modal.tsx';
import { QRScanner } from './ui/QRScanner.tsx';
import { SeatList } from './ui/SeatList.tsx';
import { Spinner } from './ui/Spinner.tsx';
import { useToast } from './ui/Toast.tsx';

export function OnayMasasi() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(params.get('focus'));
  const [scanOpen, setScanOpen] = useState(params.get('tara') === '1');
  const [scanBusy, setScanBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const { items, loading, hasQuery } = useReservationSearch(query);
  const { state, reload } = useReservationDetail(selectedId);

  // Clear one-shot query params once consumed so refreshes are clean.
  // biome-ignore lint/correctness/useExhaustiveDependencies: read URL params once on mount
  useEffect(() => {
    if (params.get('tara') || params.get('focus')) {
      setParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!selectedId) inputRef.current?.focus();
  }, [selectedId]);

  const pick = (item: ReservationListItem) => {
    setSelectedId(item.id);
    setQuery('');
  };

  const onScan = async (raw: string) => {
    const { reservationId, reference } = parseScan(raw);
    setScanBusy(true);
    try {
      if (reservationId) {
        setSelectedId(reservationId);
      } else if (reference) {
        const id = await findByReference(reference);
        if (id) setSelectedId(id);
        else toast.error('Bu karekoda ait rezervasyon bulunamadı');
      } else {
        toast.error('Karekod okunamadı');
      }
    } finally {
      setScanBusy(false);
      setScanOpen(false);
    }
  };

  const reset = () => {
    setSelectedId(null);
    setQuery('');
  };

  return (
    <div className="mx-auto max-w-3xl">
      <AnimatePresence mode="wait">
        {!selectedId ? (
          <motion.div
            key="search"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="pt-2 text-center">
              <p className="kicker mb-3">Onay Masası</p>
              <h1 className="font-display text-3xl font-medium tracking-tight md:text-4xl">
                Ziyaretçiyi bulun
              </h1>
              <p className="mx-auto mt-2 max-w-md text-sm text-text-muted">
                Telefon, referans kodu veya ad ile arayın — ya da ön rezervasyon karekodunu okutun.
              </p>
            </div>

            <div className="mt-8 flex gap-2">
              <div className="relative flex-1">
                <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-4 h-5 w-5 text-text-faint" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  inputMode="search"
                  placeholder="0532…, OKN-… veya ad soyad"
                  className="input h-14 rounded-lg pl-12 text-base"
                  // biome-ignore lint/a11y/noAutofocus: front-desk speed
                  autoFocus
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Temizle"
                    className="-translate-y-1/2 absolute top-1/2 right-3 rounded-md p-1.5 text-text-faint hover:text-text"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                size="lg"
                variant="secondary"
                icon={<ScanLine className="h-5 w-5" />}
                className="h-14 px-5"
                onClick={() => setScanOpen(true)}
              >
                <span className="hidden sm:inline">Tara</span>
              </Button>
            </div>

            <div className="mt-4">
              {loading && (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-text-faint">
                  <Spinner /> Aranıyor…
                </div>
              )}

              {!loading && hasQuery && items.length === 0 && (
                <EmptyState
                  icon={<Search className="h-6 w-6" />}
                  title="Eşleşen rezervasyon yok"
                  description="Telefonu veya referans kodunu kontrol edin. Karekod okutmayı da deneyebilirsiniz."
                />
              )}

              {!loading && items.length > 0 && (
                <div className="space-y-2">
                  {items.map((item, i) => (
                    <motion.button
                      type="button"
                      key={item.id}
                      onClick={() => pick(item)}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="group flex w-full items-center gap-4 rounded-lg border border-border bg-bg-elevated px-4 py-3.5 text-left transition-colors hover:border-border-strong hover:bg-surface"
                    >
                      <span className="mono shrink-0 text-sm text-accent tnum">
                        {item.referenceCode}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-text">{item.fullName}</p>
                        <p className="mono mt-0.5 text-xs text-text-faint tnum">{item.phone}</p>
                      </div>
                      <span className="mono hidden text-xs text-text-muted tnum sm:block">
                        {item.seatCount} koltuk
                      </span>
                      <StatusBadge status={item.status} />
                    </motion.button>
                  ))}
                </div>
              )}

              {!hasQuery && !loading && (
                <EmptyState
                  className="py-20"
                  icon={<Ticket className="h-6 w-6" />}
                  title="Aramaya başlayın"
                  description="Ziyaretçinin telefon numarasını yazın ya da karekodu okutun."
                />
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="card"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              onClick={reset}
              className="mb-4 inline-flex items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-text"
            >
              <ArrowLeft className="h-4 w-4" /> Yeni arama
            </button>

            {state.status === 'loading' && (
              <div className="flex items-center justify-center gap-2 py-24 text-sm text-text-faint">
                <Spinner /> Rezervasyon yükleniyor…
              </div>
            )}
            {state.status === 'error' && (
              <EmptyState
                icon={<X className="h-6 w-6" />}
                title="Rezervasyon açılamadı"
                description={state.message}
                action={
                  <Button variant="secondary" onClick={reset}>
                    Geri dön
                  </Button>
                }
              />
            )}
            {state.status === 'ready' && (
              <ApprovalCard data={state.data} onChanged={reload} onDone={reset} />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Modal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        title="Karekod Tara"
        subtitle="Ön rezervasyon belgesindeki karekodu kameraya gösterin"
        size="lg"
      >
        <div className="p-5">
          <QRScanner active={scanOpen} onResult={onScan} />
          {scanBusy && (
            <p className="mt-3 flex items-center justify-center gap-2 text-sm text-text-muted">
              <Spinner /> Rezervasyon bulunuyor…
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

function ApprovalCard({
  data,
  onChanged,
  onDone,
}: {
  data: import('./lib/types.ts').ReservationDetailData;
  onChanged: () => void;
  onDone: () => void;
}) {
  const r = data.reservation;
  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isPaid = r.status === 'paid';
  const isCancelled = r.status === 'cancelled';

  const approve = async () => {
    setBusy(true);
    try {
      await adminApi(`/admin/reservations/${r.id}/mark-paid`, { method: 'PATCH', body: {} });
      toast.success('Rezervasyon onaylandı');
      // Approving dispatches the invitation e-mail (fire-and-forget).
      // TODO(omer): a dedicated invitation document/template after approval.
      adminApi(`/admin/reservations/${r.id}/resend-email`, { method: 'POST', body: {} })
        .then(() => toast.success('Davetiye e-postası gönderildi'))
        .catch(() => toast.error('Davetiye gönderilemedi — yeniden deneyin'));
      setConfirmOpen(false);
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const res = await adminApi<{ delivered: boolean }>(
        `/admin/reservations/${r.id}/resend-email`,
        { method: 'POST', body: {} },
      );
      toast.success(res.delivered ? 'E-posta gönderildi' : 'E-posta kuyruğa alındı');
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!confirm(`${r.referenceCode} iptal edilsin mi? Koltuklar tekrar satışa açılır.`)) return;
    setBusy(true);
    try {
      await adminApi(`/admin/reservations/${r.id}/cancel`, {
        method: 'PATCH',
        body: { reason: 'onay masası iptal' },
      });
      toast.success('Rezervasyon iptal edildi');
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-bg-elevated">
      {/* Identity header */}
      <div className="relative border-b border-border px-6 py-5">
        {isPaid && (
          <div className="-rotate-6 pointer-events-none absolute top-4 right-5 select-none rounded-md border-2 border-success/50 px-3 py-1 font-display text-success text-sm uppercase tracking-[0.2em] opacity-90">
            Onaylandı
          </div>
        )}
        <p className="kicker mb-1.5">Referans</p>
        <div className="flex flex-wrap items-center gap-3">
          <span className="mono text-2xl text-accent tnum">{r.referenceCode}</span>
          <StatusBadge status={r.status} size="lg" />
        </div>
        <h2 className="mt-3 font-display text-2xl tracking-tight">{r.fullName}</h2>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-text-muted">
          <a
            href={`tel:${r.phone}`}
            className="mono inline-flex items-center gap-1.5 tnum hover:text-text"
          >
            <Phone className="h-3.5 w-3.5" /> {r.phone}
          </a>
          {r.guardianName && (
            <span className="inline-flex items-center gap-1.5">Veli: {r.guardianName}</span>
          )}
          {r.email && (
            <span className="inline-flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" /> {r.email}
            </span>
          )}
        </div>
      </div>

      {/* Seats */}
      <div className="grid gap-px bg-border sm:grid-cols-[200px_1fr]">
        <div className="flex flex-col justify-center bg-bg-elevated px-6 py-6">
          <p className="kicker">Koltuk</p>
          <p className="mt-1 font-display text-6xl leading-none tnum">{r.seatCount}</p>
          <p className="mt-2 text-xs text-text-faint">Nakit ödeme alın</p>
        </div>
        <div className="bg-bg-elevated px-6 py-6">
          <div className="mb-3 flex items-center justify-between">
            <p className="kicker">Koltuklar</p>
            {r.status !== 'cancelled' && (
              <Button
                variant="secondary"
                size="sm"
                icon={<Pencil className="h-3.5 w-3.5" />}
                onClick={() => setEditorOpen(true)}
              >
                Düzenle
              </Button>
            )}
          </div>
          <SeatList seats={data.seats} size="lg" />
        </div>
      </div>

      {r.note && (
        <div className="border-t border-border bg-surface px-6 py-3 text-sm">
          <span className="kicker mr-2">Not</span>
          {r.note}
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-border px-6 py-5">
        {isCancelled ? (
          <Badge tone="danger" dot>
            Bu rezervasyon iptal edilmiş
          </Badge>
        ) : isPaid ? (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-success">
              <BadgeCheck className="h-5 w-5" />
              Ödeme alındı{r.paidAt ? ` · ${fmtDateTime(r.paidAt)}` : ''}
            </div>
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                size="md"
                icon={<Send className="h-4 w-4" />}
                loading={busy}
                onClick={resend}
              >
                Davetiyeyi yeniden gönder
              </Button>
              <Button variant="secondary" size="md" onClick={onDone}>
                Bitir
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              variant="primary"
              size="xl"
              icon={<Banknote className="h-5 w-5" />}
              onClick={() => setConfirmOpen(true)}
              className="flex-1"
            >
              Ödemeyi Al ve Onayla
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="lg"
                icon={<Send className="h-4 w-4" />}
                loading={busy}
                onClick={resend}
              >
                <span className="hidden md:inline">E-posta</span>
              </Button>
              <Button variant="danger" size="lg" onClick={cancel}>
                İptal
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Ödemeyi onayla"
        subtitle={`${r.referenceCode} · ${r.seatCount} koltuk`}
      >
        <div className="px-5 py-4">
          <p className="text-sm text-text-muted">
            Nakit ödemeyi aldıysanız onaylayın. Onaylandığında koltuklar kesinleşir ve ziyaretçiye
            davetiye e-postası gönderilir.
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Vazgeç
            </Button>
            <Button
              variant="gold"
              icon={<BadgeCheck className="h-4 w-4" />}
              loading={busy}
              onClick={approve}
            >
              Evet, onayla
            </Button>
          </div>
        </div>
      </Modal>

      <SeatEditor
        open={editorOpen}
        reservation={{
          id: r.id,
          referenceCode: r.referenceCode,
          fullName: r.fullName,
          status: r.status,
          seats: data.seats,
        }}
        onClose={() => setEditorOpen(false)}
        onSaved={onChanged}
      />
    </div>
  );
}
